import React from 'react';
import { CheckIcon, GiftIcon, WrenchIcon } from 'lucide-react';
import { SectionHeading } from '../SectionHeading';
import { CheckoutButton } from '../CheckoutButton';
import { COMMERCIAL_RIGHTS, HERO_MOCKUP, OFFER_FEATURES, VOLUMES } from '../../data/welding';
import { PRICE } from '../../data/checkout';

/**
 * Value stack + first price reveal (#offer). The hero CTA scrolls here.
 * `#offer-price` and `#offer-cta` drive the mobile sticky bar.
 */
export function OfferSection() {
  return <section id="offer" aria-labelledby="offer-title" className="blueprint-grid bg-charcoal py-14 sm:py-20 lg:py-24" data-id="element-188" data-mp="1pybkwsjk1iq">
      <div className="mx-auto w-full max-w-5xl px-5 sm:px-8" data-id="element-189" data-mp="3sxudf0aglhgd">
        <SectionHeading id="offer-title" align="center" title="OBTENEZ LA COLLECTION COMPLÈTE DE PLANS D’ATELIER" data-id="element-190" data-mp="3nxpwj95n2kpm" />

        <div className="mt-8 grid grid-cols-1 overflow-hidden rounded-[14px] bg-surface shadow-[0_40px_80px_-40px_rgba(0,0,0,0.9)] ring-1 ring-arc/25 sm:mt-10 lg:grid-cols-2" data-id="element-191" data-mp="3iz4r8vbj5aub">
          <div className="hidden items-center justify-center border-r border-white/10 p-8 lg:flex" data-id="element-192" data-mp="1as0m80ktheyw">
            <img src={HERO_MOCKUP.src} alt={HERO_MOCKUP.alt} width={HERO_MOCKUP.width} height={HERO_MOCKUP.height} loading="lazy" decoding="async" className="max-h-[520px] w-auto max-w-full object-contain drop-shadow-[0_24px_30px_rgba(0,0,0,0.5)]" data-id="element-193" data-mp="1fiqt5i8w5pvp" />
          </div>

          <div className="px-5 pb-7 pt-6 sm:px-10 sm:pb-10 sm:pt-9" data-id="element-194" data-mp="1tgfd8nv4wszp">
            <p className="wordmark text-[1.55rem] text-white sm:text-[1.9rem]" data-id="element-195" data-mp="1yga4isitnkla">
              Plans de Soudure<sup className="ml-0.5 font-sans text-[0.45em] font-semibold" data-id="element-196" data-mp="1kh21fojjfsbt">™</sup>
            </p>
            <p className="mt-2 font-heading text-[1.2rem] font-semibold uppercase tracking-[0.03em] text-orange" data-id="element-197" data-mp="1oidr1u2ewdk0">
              30 projets en 6 volumes numériques
            </p>

            <ul className="mt-5 grid grid-cols-6 gap-1.5 lg:hidden" aria-label="Les six volumes" data-id="element-198" data-mp="d4ifswemooy2">
              {VOLUMES.map(volume => <li key={volume.number} data-id="element-199" data-mp="2fq5mhw0mn9jj">
                  <img src={volume.src} alt={volume.alt} width={volume.width} height={volume.height} loading="lazy" decoding="async" className="aspect-[4/5] w-full rounded-[3px] object-contain ring-1 ring-white/10" data-id="element-200" data-mp="2as46xl2etsct" />
                </li>)}
            </ul>

            <ul className="mt-5 grid gap-3" data-id="element-201" data-mp="1s0ba1fk7zmzt">
              {OFFER_FEATURES.map(feature => <li key={feature} className="flex items-start gap-3 text-[1rem] font-medium text-white" data-id="element-202" data-mp="1n3b557z3wqaa">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange text-charcoal" aria-hidden data-id="element-203" data-mp="1i4goabkp9bst">
                    <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-204" data-mp="3cihkeoov7kp7" />
                  </span>
                  {feature}
                </li>)}
            </ul>

            <p className="mt-5 flex items-center gap-3 rounded-[8px] bg-arc/10 px-4 py-3 text-[1rem] font-semibold text-white ring-1 ring-arc/35" data-id="element-205" data-mp="3ozabjrta2eay">
              <GiftIcon className="h-5 w-5 shrink-0 text-arc-light" aria-hidden data-id="element-206" data-mp="3trac9mh69b8l" />
              Plus les 5 ressources bonus
            </p>

            <p className="mt-3 flex items-start gap-3 rounded-[8px] bg-arc/10 px-4 py-3 ring-1 ring-arc/35" data-id="element-207" data-mp="31zm83n8jrv0u">
              <WrenchIcon className="mt-0.5 h-5 w-5 shrink-0 text-arc-light" aria-hidden data-id="element-208" data-mp="36061qgtwwgi8" />
              <span data-id="element-209" data-mp="3azhms21ba85m">
                <span className="label-tech block text-arc-light" data-id="element-210" data-mp="3ayzfmad429ix">{COMMERCIAL_RIGHTS.label}</span>
                <span className="mt-1 block text-[0.92rem] leading-snug text-white" data-id="element-211" data-mp="3g059ibiic2u1">{COMMERCIAL_RIGHTS.sentence}</span>
              </span>
            </p>

            <div id="offer-price" className="mt-6 border-t border-white/10 pt-6 text-center" data-id="element-212" data-mp="v8wgcdfpkg27">
              <p className="flex items-baseline justify-center gap-3 whitespace-nowrap" data-id="element-213" data-mp="vappq0hphedw">
                <span className="sr-only" data-id="element-214" data-mp="247xnqe5gr8w2">Prix normal </span>
                <s className="font-heading text-[1.9rem] font-semibold leading-none text-steel-light decoration-steel-light decoration-2 sm:text-[2.2rem]" data-id="element-215" data-mp="1z7rz76uhq2i7">
                  {PRICE.original}
                </s>
                <span className="sr-only" data-id="element-216" data-mp="285vbrdnhzoxv">, maintenant </span>
                <span className="font-heading text-[4rem] font-bold leading-none tracking-[-0.01em] text-white sm:text-[4.6rem]" data-id="element-217" data-mp="2n45szhyh7jrd">
                  {PRICE.label}
                </span>
              </p>
              <p className="mt-2 font-heading text-[1.1rem] font-semibold uppercase tracking-[0.06em] text-orange" data-id="element-218" data-mp="2vxiuh8alvtp2">
                −50 % — Cette semaine seulement
              </p>
              <p className="mt-1 text-[0.95rem] text-steel-pale" data-id="element-219" data-mp="35uxc3b89moiy">{PRICE.note}</p>
              <p className="mx-auto mt-3 inline-flex flex-wrap justify-center gap-x-1.5 rounded-[8px] bg-arc/10 px-3.5 py-2 text-[0.95rem] font-semibold leading-snug text-white ring-1 ring-arc/35" data-id="element-220" data-mp="35sub5w5krazg">
                <span data-id="element-221" data-mp="3kg1fhj0k1apd">30 projets d’atelier.</span>
                <span data-id="element-222" data-mp="3pfxibeximw3c">{PRICE.label} au total.</span>
                <span className="text-orange" data-id="element-223" data-mp="3pemfbhbw9sda">Soit {PRICE.perProject} par projet.</span>
              </p>
              <p className="mt-1.5 text-[0.8rem] text-steel-light" data-id="element-224" data-mp="3fisvdcvuiwlh">
                Moyenne par projet sur l’ensemble de la collection.
              </p>
              <p className="mx-auto mt-2 max-w-[34ch] text-[0.9rem] leading-snug text-steel-light" data-id="element-225" data-mp="31qmgdu4fivnm">
                Recevez les 6 volumes PDF et les 5 bonus par e-mail juste après le paiement.
              </p>
            </div>

            <CheckoutButton id="offer-cta" label="OBTENIR LA COLLECTION COMPLÈTE" size="lg" className="mt-6 w-full" data-id="element-226" data-mp="2rr3z5lfwlnz9" />

            <p className="mt-4 text-center text-[0.86rem] leading-relaxed text-steel-light" data-id="element-227" data-mp="2isifto2ly4xf">
              Plans numériques pour fabriquer des équipements d’atelier. Les équipements et outils physiques ne sont pas inclus.
            </p>
          </div>
        </div>
      </div>
    </section>;
}